import { useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for debounce that avoids the isMounted antipattern.
 * Properly cleans up timers instead of checking if component is mounted.
 *
 * @param callback - function to debounce.
 * @param delay - delay in milliseconds.
 * @returns debounced function.
 *
 * @example
 * // With parameters
 * const debouncedSave = useDebounce((value: string) => saveData(value), 500);
 * debouncedSave("hello");
 *
 * // Without parameters
 * const debouncedTrack = useDebounce(() => trackEvent(), 1000);
 * debouncedTrack();
 */

function useDebounce<T extends unknown[]>(
  callback: (...args: T) => void,
  delay: number,
): (...args: T) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);

  // Keep callback ref current
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const debouncedCallback = useCallback(
    (...args: T) => {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
        timeoutRef.current = null;
      }, delay);
    },
    [delay],
  );

  return debouncedCallback;
}

export default useDebounce;
